import { EventEmitter, Injectable } from '@angular/core';
import { AuthenticationService } from './authentication.service';
import {ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot} from '@angular/router';
import { IMe } from '../types/api-response/auth/me';
import { Permission } from '../types/api-response/auth/permission';

@Injectable()
export class AccountPermissionsService {
    public onPermissionsUpdate: EventEmitter<any> = new EventEmitter();
    public user: any = JSON.parse(localStorage.getItem('userData')) || {};
    public services: any = this.getAllAvailableServices() || {};

    constructor(public authService: AuthenticationService) { }

    public init(): void {
        this.authService.user$.subscribe(user => {
            this.user = user || {};
            this.services = this.getAllAvailableServices() || {};
            this.onPermissionsUpdate.emit();
        });
    }

    // TODO: replace with this.authService.user$
    public getUser(): IMe|null {
        return this.authService.user;
    }

    // TODO: remove! only one service must do that
    public saveUser(user: string): void {
        localStorage.setItem('userData', user);
        this.user = this.getUser() || {};
    }

    private getAllAvailableServices(): any {
        const availableServices = {};
        const availableServicesResident = [];
        if (this.user.buildingPropertyManager) {
            this.user.buildingPropertyManager.forEach(item => {
                if (item.rolePermissions) {
                    Object.keys(item.rolePermissions).forEach(role => {
                        if (item.rolePermissions[role].r == true || item.rolePermissions[role].c == true) {
                            availableServices[role] = true;
                        }
                        if(!this.authService.userIsResident)
                        if (item.role.settings[role].c == true)
                            availableServicesResident.push(role);
                    });
                }
            });
        }
        return availableServices;
    }

    private getCurrentBuildingPropertyManager(buildingId: number): any {

        return this.user.buildingPropertyManager.find(item => +item.buildingId === +buildingId);
    }

    /**
     * Returns Set of permissions for current user
     * for each corporation number ID
     * for selected service
     */
    public getCorporationServicePermissions(serviceName): { [key: number]: Set<Permission> } {
        const result = {};
        (this.user as IMe).buildingPropertyManager.filter(buildingPm => buildingPm.role).forEach(buildingPm => {
            const corporationNumberId = buildingPm.role.corporationNumberId;
            const set: Set<string> = result[corporationNumberId] || new Set();
            const permissions = buildingPm.rolePermissions[serviceName];

            if(permissions) {
                Object.getOwnPropertyNames(permissions).filter(key => permissions[key]).forEach(key => set.add(key));
            }

            result[corporationNumberId] = set;
        });

        return result;
    }

    private isServiceOperationAllowed(serviceName: string, operation: string = 'read', buildingId?): boolean {
        if (this.isUserAdmin() || this.isUserCompanyAdmin()) {
            return true;
        }

        if (!this.isServiceAllowed(serviceName)) {
            return false;
        }

        if (!this.user.buildingPropertyManager) {
            return false;
        }

        if (buildingId === undefined) {
            for (let i = 0; i < this.user.buildingPropertyManager.length; i++) {
                if(this.user.buildingPropertyManager[i].rolePermissions) {
                    const permission = this.user.buildingPropertyManager[i].rolePermissions[serviceName];

                    switch (operation) {
                        case 'read':
                            if (permission && permission.r) return true;
                            break;

                        case 'update':
                            if (permission && permission.u && permission.c) return true;
                            break;

                        case 'delete':
                            if (permission && permission.d) return true;
                            break;

                        case 'approve':
                            if (permission && permission.a) return true;
                            break;

                        case 'settings':
                            if (permission && permission.s) return true;
                            break;
                    }
                }
            }

            return false;
        }

        const currentBuildingPropertyManager = this.getCurrentBuildingPropertyManager(buildingId),
            currentService = currentBuildingPropertyManager && currentBuildingPropertyManager.rolePermissions[serviceName];

        switch (operation) {
            case 'read':
                return !!(currentService && currentService.r);

            case 'update':
                return !!(currentService && currentService.c && currentService.u);

            case 'delete':
                return !!(currentService && currentService.d);

            case 'approve':
                return !!(currentService && currentService.a);
        }

        return false;
    }

    public isServiceAllowed(serviceName: string): boolean {
        return !!this.services[serviceName] || this.isUserAdmin() || this.isUserCompanyAdmin();
    }

    public canRead(serviceName, buildingId?): boolean {

        return this.isServiceOperationAllowed(serviceName, 'read', buildingId);
    }

    public canUpdate(serviceName, buildingId?): boolean {

        return this.isServiceOperationAllowed(serviceName, 'update', buildingId);
    }

    public canDelete(serviceName, buildingId?): boolean {

        return this.isServiceOperationAllowed(serviceName, 'delete', buildingId);
    }

    public canApprove(serviceName, buildingId?): boolean {

        return this.isServiceOperationAllowed(serviceName, 'approve', buildingId);
    }

    public canSetup(serviceName: string, buildingId?: number): boolean {

        return this.isServiceOperationAllowed(serviceName, 'settings', buildingId);
    }

    public getBuildingsForRead(entities: any, serviceName: string): Array<any> {

        return entities.filter(item => this.canRead(serviceName, item.id));
    }

    public getBuildingsForUpdate(entities: any, serviceName: string): Array<any> {

        return entities.filter(item => this.canUpdate(serviceName, item.id));
    }

    public isUserAdmin(): boolean {
        return this.user && this.user.userType === 'super_admin';
    }

    public isUserCompanyAdmin(): boolean {
        return this.user && this.user.userType === 'company_admin';
    }

    public navigateIfServiceDisallowed(serviceName: string, path: string = '/home') {
        if (serviceName && !this.isServiceAllowed(serviceName)) {
            this.authService.router.navigate([path]);

            return false;
        }

        return true;
    }

    public addEntitiesPermissions(entities: Array<any>, serviceName: string): void {
        if (entities) {
            for (let i = 0; i < entities.length; i++) {
                entities[i] = this.addPermissionsToEntity(entities[i], serviceName);
            }
        }
    }

    public addPermissionsToEntity(entity, serviceName) {
        const buildingId = (entity.building && entity.building.id)
            ? entity.building.id
            : entity.building;
        entity['isCanEdit'] = this.canUpdate(serviceName, buildingId);
        entity['isCanDelete'] = this.canDelete(serviceName, buildingId);
        entity['isCanApprove'] = this.canApprove(serviceName, buildingId);
        return entity;
    }
}

@Injectable()
export class NavGuard implements CanActivate {

    constructor(private account: AccountPermissionsService) {
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const isServiceAllowed = this.account.isServiceAllowed(route.data.service);

        if (isServiceAllowed &&
            route.data.service === 'auth' &&
            route.data.adminOnly &&
            !this.account.isUserAdmin() && !this.account.isUserCompanyAdmin()) return false;

        return isServiceAllowed;
    }
}

@Injectable()
export class NavGuardSettings implements CanActivate {

    constructor(private account: AccountPermissionsService) {
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {

        return this.account.canSetup(route.data.service);
    }
}

@Injectable()
export class CanCreateGuard implements CanActivate {

    constructor(private account: AccountPermissionsService) {
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {

        return this.account.canUpdate(route.data.service);
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/account-permissions.service.ts